import { addDays, daysBetween, startOfWeek } from '@fd/finance-core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { today, useDailySpending, type DaySpending } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { navigate } from '../app/router.tsx';
import { Card, Money } from '../ui/primitives.tsx';
import { useInTile } from '../ui/tile.ts';

/** Room for the weekday names on the left and the month names on top. */
const LEFT = 32;
const TOP = 18;
/** A day's square with its gap, in pixels: sized to the card between these (the owner: "it does not scale"). */
const MIN_STEP = 9;
const MAX_STEP = 34;
/** The square's share of its step; the rest is the gap. */
const FILL = 0.8;
/** One view, the whole year (the owner: "only one view, the whole year"). */
const YEAR = 53;
type HeatmapCell = { date: string; value: bigint; count: number };
/** `x`, `top`, `bottom`: where the day's square is on the screen (viewport pixels). */
type DayTooltip = { day: DaySpending; x: number; top: number; bottom: number; left: number; right: number };

/**
 * The room a tip has sideways: the box of the nearest part of the page that scrolls, less its scroll
 * bar (the window's width counts the bar, and a tip pushed under it is half hidden).
 */
function roomOf(element: Element): { left: number; right: number } {
  for (let up = element.parentElement; up && up !== document.body; up = up.parentElement) {
    const { overflowY } = getComputedStyle(up);
    if ((overflowY === 'auto' || overflowY === 'scroll') && up.scrollHeight > up.clientHeight) {
      const box = up.getBoundingClientRect();
      return { left: box.left + up.clientLeft, right: box.left + up.clientLeft + up.clientWidth };
    }
  }
  return { left: 0, right: document.documentElement.clientWidth };
}

/** The gap kept between the tip and the edge of the window, and between the tip and its square. */
const EDGE = 8;

/**
 * The tip over a day's square. It lives above the page (not inside the card, where the card's edge
 * cut it off and made scroll bars), centred on the square and kept inside the window: moved
 * sideways at the edges, and under the square when there is no room above it.
 */
function DayTip({ at, children }: { at: DayTooltip; children: ReactNode }) {
  const element = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const tip = element.current;
    if (!tip) return;
    const { width, height } = tip.getBoundingClientRect();
    const left = Math.max(at.left + EDGE, Math.min(at.right - width - EDGE, at.x - width / 2));
    const above = at.top - height - EDGE;
    setPlace({ left, top: above >= EDGE ? above : at.bottom + EDGE });
  }, [at]);
  return createPortal(
    <div ref={element} className="chart-tooltip heatmap__tooltip" style={{ left: place?.left ?? 0, top: place?.top ?? 0, visibility: place ? 'visible' : 'hidden' }} aria-hidden="true">
      {children}
    </div>,
    document.body,
  );
}

/** Four magnitude steps from the quartiles of days with spending (days without stay empty). */
function thresholds(values: readonly bigint[]): [bigint, bigint, bigint] {
  const sorted = values.filter((v) => v > 0n).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  if (sorted.length === 0) return [0n, 0n, 0n];
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]!;
  return [at(0.25), at(0.5), at(0.75)];
}

function level(value: bigint, [q1, q2, q3]: [bigint, bigint, bigint]): number {
  if (value <= 0n) return 0;
  if (value <= q1) return 1;
  if (value <= q2) return 2;
  if (value <= q3) return 3;
  return 4;
}

/**
 * Spending heatmap (inspired by the owner's reference): one square per day for the last year,
 * weeks as columns starting on Monday, one hue getting darker with the amount.
 * Hover or keyboard focus shows the day; activation opens that day's transactions.
 *
 * The squares follow the space they have: as wide as the card allows, and on a dashboard card of
 * a set size as tall as it allows too; a card too narrow for every week (a phone) shows the latest.
 */
export function SpendingHeatmap({ currency, className }: { currency: string; className?: string }) {
  const t = useT();
  const inTile = useInTile();
  const wrapper = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const observer = useRef<ResizeObserver | null>(null);
  const measure = useCallback((element: HTMLDivElement | null) => {
    wrapper.current = element;
    observer.current?.disconnect();
    if (!element) return;
    observer.current = new ResizeObserver(([entry]) => setBox({ width: Math.round(entry!.contentRect.width), height: Math.round(entry!.contentRect.height) }));
    observer.current.observe(element);
  }, []);
  useEffect(() => () => observer.current?.disconnect(), []);
  const WEEKS = box.width > 0 ? Math.max(4, Math.min(YEAR, Math.floor((box.width - LEFT) / MIN_STEP))) : YEAR;
  const byWidth = box.width > 0 ? (box.width - LEFT) / WEEKS : 18;
  const STEP = Math.max(8, Math.min(MAX_STEP, inTile && box.height > 0 ? Math.min(byWidth, (box.height - TOP) / 7) : byWidth));
  const CELL = Math.max(6, Math.round(STEP * FILL));
  const end = today();
  const start = addDays(startOfWeek(end), -7 * (WEEKS - 1));
  const days = useDailySpending(start, end);
  const cellElements = useRef(new Map<string, SVGRectElement>());
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [hover, setHover] = useState<DayTooltip | null>(null);
  const [focused, setFocused] = useState<DayTooltip | null>(null);

  const model = useMemo(() => {
    if (!days) return null;
    const cells: { date: string; col: number; row: number; value: bigint; count: number }[] = [];
    for (let date = start; date <= end; date = addDays(date, 1)) {
      const offset = daysBetween(start, date);
      const day = days.get(date);
      cells.push({
        date,
        col: Math.floor(offset / 7),
        row: offset % 7,
        value: day?.spentMinor ?? 0n,
        count: day?.count ?? 0,
      });
    }
    const limits = thresholds(cells.map((c) => c.value));
    const months: { col: number; date: string }[] = [];
    for (const cell of cells) {
      if (cell.date.endsWith('-01') && cell.col < WEEKS - 1)
        months.push({ col: cell.row === 0 ? cell.col : cell.col + 1, date: cell.date });
    }
    const spending = cells.filter((c) => c.value > 0n);
    const total = cells.reduce((sum, c) => sum + (c.value > 0n ? c.value : 0n), 0n);
    const biggest = spending.reduce<(typeof cells)[number] | null>(
      (best, c) => (!best || c.value > best.value ? c : best),
      null,
    );
    return {
      cells: cells.map((c) => ({ ...c, level: level(c.value, limits) })),
      months,
      average: cells.length > 0 ? total / BigInt(cells.length) : 0n,
      biggest,
      without: cells.length - spending.length,
    };
  }, [days, start, end, WEEKS]);

  const width = LEFT + WEEKS * STEP;
  const height = TOP + 7 * STEP;
  // The weeks start on Monday: row 0 is Monday, row 6 Sunday. Short names of our own (Pn … Nd), not the browser's.
  const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
  const weekdays = [0, 2, 4, 6].map((row) => ({ row, label: t.dynamic(`heatmap.days.${DAYS[row]}`) }));
  const tabDate = model?.cells.some((cell) => cell.date === activeDate) ? activeDate : end;
  const tooltip = hover ?? focused;
  const tooltipAt = (target: SVGRectElement, cell: HeatmapCell): DayTooltip => {
    const bounds = target.getBoundingClientRect();
    return {
      day: { date: cell.date, spentMinor: cell.value, count: cell.count },
      x: bounds.left + bounds.width / 2,
      top: bounds.top,
      bottom: bounds.bottom,
      ...roomOf(target),
    };
  };
  // A tip left where it was while the page scrolls or resizes would point at nothing.
  useEffect(() => {
    const away = () => {
      setHover(null);
      setFocused(null);
    };
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, []);

  return (
    <Card
      className={['heatmap-card', className].filter(Boolean).join(' ')}
      title={t.t('heatmap.title')}
      subtitle={WEEKS < YEAR ? t.plural('heatmap.lastWeeks', WEEKS) : t.t('heatmap.subtitle')}
    >
      {!model ? (
        <div className="skeleton" style={{ height: 150 }} />
      ) : (
        <div className="stack gap-4">
          <div className="heatmap-stats">
            <div>
              <span className="list-row__meta">{t.t('heatmap.dailyAverage')}</span>
              <strong>
                <Money minor={model.average} currency={currency} />
              </strong>
            </div>
            {model.biggest && (
              <div>
                <span className="list-row__meta">{t.t('heatmap.biggestDay')}</span>
                <strong>
                  <Money minor={model.biggest.value} currency={currency} />
                  <span className="heatmap-stats__date">
                    {' '}
                    · {t.date(model.biggest.date, 'dayMonth')}
                  </span>
                </strong>
              </div>
            )}
            <div>
              <span className="list-row__meta">{t.t('heatmap.daysWithout')}</span>
              <strong>{new Intl.NumberFormat(t.locale).format(model.without)}</strong>
            </div>
          </div>
          <div className="heatmap" ref={measure} onMouseLeave={() => setHover(null)}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              style={{ width, height }}
              role="group"
              aria-label={
                model.biggest
                  ? t.t('heatmap.summary', {
                      date: t.date(model.biggest.date, 'long'),
                      amount: t.money({ minor: model.biggest.value, currency }),
                    })
                  : t.t('heatmap.subtitle')
              }
            >
              {model.months.map((m) => (
                <text
                  key={m.date}
                  x={LEFT + m.col * STEP}
                  y={11}
                  className="heatmap__label"
                  aria-hidden="true"
                >
                  {t.date(m.date, 'month')}
                </text>
              ))}
              {weekdays.map((w) => (
                <text
                  key={w.row}
                  x={0}
                  y={TOP + w.row * STEP + CELL - 2}
                  className="heatmap__label"
                  aria-hidden="true"
                >
                  {w.label}
                </text>
              ))}
              {model.cells.map((cell, index) => (
                <rect
                  key={cell.date}
                  ref={(element) => {
                    if (element) cellElements.current.set(cell.date, element);
                    else cellElements.current.delete(cell.date);
                  }}
                  x={LEFT + cell.col * STEP}
                  y={TOP + cell.row * STEP}
                  width={CELL}
                  height={CELL}
                  rx={3}
                  className="heatmap__cell"
                  fill={`var(--heat-${cell.level})`}
                  role="button"
                  tabIndex={cell.date === tabDate ? 0 : -1}
                  aria-label={[
                    t.t('heatmap.openDay', { date: t.date(cell.date, 'long') }),
                    cell.value > 0n
                      ? t.money({ minor: cell.value, currency })
                      : t.t('heatmap.noSpending'),
                    t.plural('transactions.count', cell.count),
                  ].join('. ')}
                  onMouseEnter={(event) => setHover(tooltipAt(event.currentTarget, cell))}
                  onFocus={(event) => {
                    setActiveDate(cell.date);
                    setHover(null);
                    setFocused(tooltipAt(event.currentTarget, cell));
                  }}
                  onBlur={() => setFocused(null)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate({ page: 'transactions', date: cell.date });
                      return;
                    }
                    if (event.key === 'Escape') {
                      setHover(null);
                      setFocused(null);
                      return;
                    }
                    // The calendar is one Tab stop; arrows follow its day/week layout.
                    const nextIndex =
                      event.key === 'ArrowUp'
                        ? index - 1
                        : event.key === 'ArrowDown'
                          ? index + 1
                          : event.key === 'ArrowLeft'
                            ? index - 7
                            : event.key === 'ArrowRight'
                              ? index + 7
                              : event.key === 'Home'
                                ? 0
                                : event.key === 'End'
                                  ? model.cells.length - 1
                                  : null;
                    if (nextIndex === null) return;
                    event.preventDefault();
                    const next =
                      model.cells[Math.max(0, Math.min(model.cells.length - 1, nextIndex))];
                    if (next) cellElements.current.get(next.date)?.focus();
                  }}
                  onClick={() => navigate({ page: 'transactions', date: cell.date })}
                >
                  <title>{t.t('heatmap.openDay', { date: t.date(cell.date, 'long') })}</title>
                </rect>
              ))}
            </svg>
            {tooltip && (
              <DayTip at={tooltip}>
                <div className="chart-tooltip__title">{t.date(tooltip.day.date, 'dayHeading')}</div>
                <div className="row gap-2">
                  <strong className="amount-text">
                    {tooltip.day.spentMinor > 0n
                      ? t.money({ minor: tooltip.day.spentMinor, currency })
                      : t.t('heatmap.noSpending')}
                  </strong>
                  {tooltip.day.count > 0 && (
                    <span className="text-muted">
                      · {t.plural('transactions.count', tooltip.day.count)}
                    </span>
                  )}
                </div>
              </DayTip>
            )}
          </div>
          <div className="heatmap-legend" aria-hidden="true">
            <span>{t.t('heatmap.less')}</span>
            {[0, 1, 2, 3, 4].map((l) => (
              <span
                key={l}
                className="heatmap-legend__swatch"
                style={{ background: `var(--heat-${l})` }}
              />
            ))}
            <span>{t.t('heatmap.more')}</span>
          </div>
        </div>
      )}
    </Card>
  );
}
